/**
 * Redaktionelle Texte einer Produktfamilie.
 *
 * Hier gibt es keine Vererbung -- die Familie ist die Quelle. Dargestellt
 * werden ausschliesslich die Texttypen, die das Modell auf Familienebene
 * zulaesst (`allowsFamily`).
 *
 * Diese Liste stammt aus `meta.textTypes` und damit aus der Datenbank. Das
 * Frontend fuehrt KEINE eigene Texttypliste; die Grenze bleibt die
 * Datenbank- und API-Regel.
 */
import { useCallback, useEffect, useState } from "react";
import type { MetaResponse, ProductText } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { ProductTexts, type TextSlot } from "./ProductTexts.tsx";

export function FamilyTexts({ familyId, meta, mayWrite, mayRelease }: {
  familyId: number;
  meta: MetaResponse;
  mayWrite: boolean;
  mayRelease: boolean;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [texts, setTexts] = useState<readonly ProductText[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try { setTexts((await api.familyTexts(familyId)).items); }
    catch (e) { setError(e); }
  }, [familyId]);

  useEffect(() => { setTexts(null); setEditing(null); void load(); }, [load]);

  if (error !== null && !texts) {
    return <div className="texts"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!texts) return <div className="texts"><LoadingState label={t("texts.loading")} /></div>;

  const byType = new Map(texts.map((x) => [x.type, x]));
  const slots: TextSlot[] = meta.textTypes
    .filter((tt) => tt.allowsFamily)
    .map((tt) => ({
      type: tt.code, maxLength: tt.maxLength, text: byType.get(tt.code) ?? null,
      inherited: false, inheritedFrom: null, editable: true,
    }));

  return (
    <div className="texts">
      <h3 className="detail__section">{t("texts.section")}</h3>
      {texts.length === 0 && !mayWrite && <p className="texts__empty">{t("texts.emptyFamily")}</p>}
      {/* Eine Familie hat keinen Supplier -- der Bezug haengt am Artikel. */}
      <ProductTexts
        slots={slots} subject={{ kind: "family", id: familyId }}
        mayWrite={mayWrite} mayRelease={mayRelease} supplier={null}
        editing={editing} setEditing={setEditing}
        onChanged={() => { setEditing(null); void load(); }}
        onError={setError}
      />
      {error !== null && <p className="texts__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}
